import { useEffect, useRef } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ModalShell } from '../../../shared/ModalShell';

interface IteroDisconnectModalProps {
    message: string;
    isPending: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

const TITLE_ID = 'disconnect-itero-title';

export function IteroDisconnectModal({
    message,
    isPending,
    onConfirm,
    onCancel
}: IteroDisconnectModalProps) {
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        cancelRef.current?.focus();
    }, []);

    return (
        <ModalShell
            widthClassName="max-w-md"
            dismissible={!isPending}
            onDismiss={onCancel}
            labelledBy={TITLE_ID}
            panelClassName="rounded-xl p-6 relative"
        >
            <button
                type="button"
                onClick={onCancel}
                disabled={isPending}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                aria-label="Close"
            >
                <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 id={TITLE_ID} className="text-lg font-semibold text-gray-900">
                        Disconnect from iTero?
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{message}</p>
                </div>
            </div>

            <div className="flex justify-end items-center gap-3 mt-6">
                <button
                    ref={cancelRef}
                    type="button"
                    onClick={onCancel}
                    disabled={isPending}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={isPending}
                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                    {isPending ? (
                        <>
                            <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                            Disconnecting…
                        </>
                    ) : (
                        'Disconnect'
                    )}
                </button>
            </div>
        </ModalShell>
    );
}
